// Translation files for English and Spanish
export const translations = {
  en: {
    // Common
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      confirm: 'Confirm',
      cancel: 'Cancel',
      delete: 'Delete',
      save: 'Save',
      update: 'Update',
      create: 'Create',
      refresh: 'Refresh',
      logout: 'Logout',
      back: 'Back',
      next: 'Next',
      continue: 'Continue',
      complete: 'Complete',
      ok: 'OK',
      yes: 'Yes',
      no: 'No',
      close: 'Close',
      edit: 'Edit',
      view: 'View',
      upload: 'Upload',
      download: 'Download',
      search: 'Search',
      filter: 'Filter',
      sort: 'Sort',
      reset: 'Reset',
      submit: 'Submit',
      login: 'Login',
      register: 'Register',
      signIn: 'Sign In',
      signUp: 'Sign Up',
      email: 'Email',
      password: 'Password',
      name: 'Name',
      phone: 'Phone',
      address: 'Address',
      date: 'Date',
      time: 'Time',
      status: 'Status',
      duration: 'Duration',
      from: 'From',
      to: 'To',
      description: 'Description',
      message: 'Message',
      title: 'Title',
      content: 'Content',
      file: 'File',
      files: 'Files',
      size: 'Size',
      type: 'Type',
      created: 'Created',
      updated: 'Updated',
      uploaded: 'Uploaded',
      deleted: 'Deleted',
      active: 'Active',
      inactive: 'Inactive',
      enabled: 'Enabled',
      disabled: 'Disabled',
      available: 'Available',
      unavailable: 'Unavailable',
      required: 'Required',
      optional: 'Optional',
      all: 'All',
      none: 'None',
      select: 'Select',
      choose: 'Choose',
      browse: 'Browse',
      clear: 'Clear',
      apply: 'Apply',
      cancel: 'Cancel',
      confirm: 'Confirm',
      warning: 'Warning',
      info: 'Information',
      notice: 'Notice',
      alert: 'Alert',
      notification: 'Notification',
      help: 'Help',
      support: 'Support',
      contact: 'Contact',
      about: 'About',
      settings: 'Settings',
      preferences: 'Preferences',
      profile: 'Profile',
      account: 'Account',
      dashboard: 'Dashboard',
      home: 'Home',
      menu: 'Menu',
      navigation: 'Navigation',
      language: 'Language',
      english: 'English',
      spanish: 'Spanish',
      selectLanguage: 'Select Language',
      changeLanguage: 'Change Language'
    },

    // Navigation
    nav: {
      clinicAssistant: 'TechMaven Clinic Assistant',
      adminPanel: 'Admin Panel',
      clinicName: 'Clinic Name'
    },

    // Login/Register
    auth: {
      signInToClinic: 'Sign in to your clinic',
      registerClinic: 'Register your clinic',
      adminPanel: 'Admin Panel',
      signInToAdmin: 'Sign in to manage clinics and system settings',
      adminEmail: 'Admin Email',
      clinicName: 'Clinic Name',
      needToRegister: 'Need to register?',
      alreadyHaveAccount: 'Already have an account?',
      adminAccessRequired: 'Admin access required',
      loginFailed: 'Login failed',
      signingIn: 'Signing in...',
      signInToAdminPanel: 'Sign in to Admin Panel',
      invalidCredentials: 'Invalid credentials',
      emailPasswordRequired: 'Email and password are required',
      emailPasswordClinicRequired: 'Email, password, and clinic name are required',
      passwordMinLength: 'Password must be at least 8 characters',
      clinicExists: 'Clinic with this email already exists',
      clinicRegistered: 'Clinic registered successfully',
      loginSuccessful: 'Login successful',
      internalServerError: 'Internal server error'
    },

    // Dashboard
    dashboard: {
      callLogs: 'Call Logs',
      recentCalls: 'Recent phone calls and conversations',
      refreshing: 'Refreshing...',
      recentCallsTitle: 'Recent Calls',
      autoRefresh: 'Auto-refreshes every 30 seconds',
      loadingCalls: 'Loading calls...',
      noCallsYet: 'No calls yet',
      callsWillAppear: 'Calls will appear here once your assistant starts receiving them',
      systemStatus: 'System Status',
      assistant: 'Assistant',
      phone: 'Phone',
      prompt: 'Prompt',
      setup: 'Setup',
      complete: 'Complete',
      quickActions: 'Quick Actions',
      createAssistant: 'Create Assistant',
      setupAiToHandleCalls: 'Set up AI to handle calls',
      systemPrompt: 'System Prompt',
      configured: 'Configured',
      notConfigured: 'Not configured',
      knowledgeBase: 'Knowledge Base',
      uploadFiles: 'Upload files',
      tools: 'Tools',
      manageTools: 'Manage tools',
      phoneNumber: 'Phone Number',
      notProvisioned: 'Not provisioned',
      areaCode: 'Area code',
      new: 'New',
      deleting: 'Deleting...',
      getNumber: 'Get Number',
      getting: 'Getting...',
      get: 'Get',
      systemInfo: 'System Info',
      model: 'Model',
      voice: 'Voice',
      clinic: 'Clinic',
      promptPreview: 'Prompt',
      tryingAreaCode: 'Trying area code {areaCode}...',
      phoneProvisioned: 'Phone Number Provisioned',
      phoneProvisionedMessage: 'Phone number successfully provisioned!\n\nNote: Your requested area code ({requestedAreaCode}) was not available, so we provisioned a number with area code {successfulAreaCode} instead.',
      confirmDelete: 'Confirm Delete',
      confirmDeleteMessage: 'Are you sure you want to delete this phone number? This action cannot be undone.',
      phoneDeleted: 'Phone number deleted successfully. You can now provision a new number.',
      failedToCreateAssistant: 'Failed to create assistant',
      failedToProvisionPhone: 'Failed to provision phone number',
      failedToDeletePhone: 'Failed to delete phone number',
      vapiAuthFailed: 'VAPI API authentication failed'
    },

    // System Prompt
    systemPrompt: {
      configureSystemPrompt: 'Configure System Prompt',
      backToDashboard: '← Back to Dashboard',
      loadingCurrentPrompt: 'Loading current prompt...',
      systemPromptLabel: 'System Prompt',
      enterSystemPrompt: `
      Role and Identity
      
      You are a [ASSISTANT ROLE OR IDENTITY]
      Your role is to [DESCRIBE PRIMARY FUNCTION OR PURPOSE]
      
      Key Guidelines
      
      Maintain a [TONE such as polite professional empathetic friendly concise]
      
      Focus on [AREA OF RESPONSIBILITY such as customer support education technical help]
      
      Do not provide restricted advice such as medical legal or financial guidance
      
      If the question is outside your scope explain your limitation and suggest alternatives
      
      Always prioritize safety confidentiality and user trust
      
      Response Style
      
      Keep answers [BRIEF DETAILED or BALANCED] depending on context
      
      Use [FORMAT such as bullet points numbered lists or conversational tone] when it improves clarity
      
      When unsure say I do not know but I can help you find out
      
      Examples
      
      If user asks about [TOPIC A] respond with [ACTION OR RESPONSE]
      
      If user asks about [TOPIC B] respond with [ACTION OR RESPONSE]
      
      If user raises an urgent or emergency situation instruct them to contact [APPROPRIATE CONTACT OR ESCALATION STEP]
      
      Reminder
      
      You are not a restricted role such as doctor lawyer or financial advisor
      
      Stay within your defined role
      
      End every interaction in a polite professional and supportive way
Example: Email Notification
when a someone contact to this assitant first thing to do 
is send an email to this address example@gmail.com using 'Send_Email_Notifcation' api request tool
Use this format:
- subject: "New Incoming Call Notification"
- body: include the caller's name (if available), phone number, and call time.
- recipient: example@gmail.com


`,
      
      characters: 'Characters',
      saving: 'Saving...',
      savePrompt: 'Save Prompt',
      systemPromptUpdated: 'System prompt updated successfully!',
      failedToUpdatePrompt: 'Failed to update system prompt'
    },

    // Knowledge Base
    knowledge: {
      uploadKnowledgeBaseFiles: 'Upload Knowledge Base Files',
      backToDashboard: '← Back to Dashboard',
      selectFile: 'Select File (PDF or TXT only, max 10MB)',
      selected: 'Selected',
      uploading: 'Uploading...',
      uploadFile: 'Upload File',
      fileUploaded: 'File uploaded successfully!',
      failedToUpload: 'Failed to upload file',
      uploadedFiles: 'Uploaded Files',
      noFilesUploaded: 'No files uploaded yet',
      uploaded: 'Uploaded',
      confirmDeleteFile: 'Are you sure you want to delete this file?',
      fileDeleted: 'File deleted successfully',
      failedToDeleteFile: 'Failed to delete file',
      noAssistantFound: 'No assistant found. Please create an assistant first.',
      onlyPdfTxtAllowed: 'Only PDF and TXT files are allowed',
      noFileUploaded: 'No file uploaded',
      pleaseSelectFile: 'Please select a file',
      fileNotFound: 'File not found',
      failedToGetFiles: 'Failed to get files'
    },

    // Tools Management
    tools: {
      manageTools: 'Manage Assistant Tools',
      backToDashboard: '← Back to Dashboard',
      addTool: 'Add Tool',
      editTool: 'Edit Tool',
      toolType: 'Tool Type',
      functionTool: 'Function Tool',
      webhookTool: 'Webhook Tool',
      functionName: 'Function Name',
      functionDescription: 'Function Description',
      parameters: 'Parameters',
      addParameter: 'Add Parameter',
      enterParameterName: 'Enter parameter name:',
      required: 'Required',
      noParameters: 'No parameters defined',
      serverUrl: 'Server URL',
      serverSecret: 'Server Secret',
      loadingTools: 'Loading tools...',
      noTools: 'No tools configured',
      noToolsDescription: 'Add tools to extend your assistant\'s capabilities',
      unnamedTool: 'Unnamed Tool',
      noDescription: 'No description provided',
      confirmDelete: 'Confirm Delete',
      confirmDeleteMessage: 'Are you sure you want to delete this tool? This action cannot be undone.',
      toolAdded: 'Tool added successfully!',
      toolUpdated: 'Tool updated successfully!',
      toolDeleted: 'Tool deleted successfully!',
      failedToSaveTool: 'Failed to save tool',
      failedToDeleteTool: 'Failed to delete tool',
      failedToFetchTools: 'Failed to fetch tools',
      toolTemplates: 'Tool Templates',
      webhookTemplate: 'Webhook Tool',
      webhookDescription: 'Connect to external webhook endpoint',
      addEmailTool: 'Add Email Notification Tool',
      emailToolAdded: 'Email notification tool added successfully!',
      failedToAddEmailTool: 'Failed to add email notification tool'
    },

    // Admin Dashboard
    admin: {
      loadingAdminPanel: 'Loading admin panel...',
      clinics: 'Clinics',
      systemPrompt: 'System Prompt',
      allClinics: 'All Clinics',
      manageClinics: 'Manage all registered clinics and their data',
      hasAssistant: '✅ Assistant',
      noAssistant: '❌ No Assistant',
      hasPhone: '✅ Phone',
      noPhone: '❌ No Phone',
      deleteClinic: 'Delete Clinic',
      deleteClinicMessage: 'Are you sure you want to delete "{clinicName}"? This will permanently delete all associated data including assistants, phone numbers, and call logs.',
      clinicDeleted: 'Clinic deleted successfully',
      failedToDeleteClinic: 'Failed to delete clinic',
      defaultSystemPrompt: 'Default System Prompt',
      defaultPromptDescription: 'This prompt will be used for all new assistants created by clinics.',
      systemPromptLabel: 'System Prompt',
      enterDefaultPrompt: 'Enter the default system prompt...',
      resetToOriginal: 'Reset to Original',
      updating: 'Updating...',
      updatePrompt: 'Update Prompt',
      defaultPromptUpdated: 'Default system prompt updated successfully',
      failedToUpdatePrompt: 'Failed to update system prompt',
      resetSystemPrompt: 'Reset System Prompt',
      resetPromptMessage: 'Are you sure you want to reset the system prompt to the original default?',
      promptReset: 'System prompt reset to original default',
      failedToResetPrompt: 'Failed to reset system prompt',
      failedToFetchPrompt: 'Failed to fetch default prompt'
    },

    // Setup Wizard
    setupWizard: {
      welcome: 'Welcome!',
      welcomeDescription: 'Let\'s get your clinic assistant set up in just a few steps.',
      welcomeToClinic: 'Welcome to {clinicName}',
      setupDescription: 'We\'ll help you set up your AI phone assistant in just 3 simple steps.',
      whatWellSetup: 'What we\'ll set up:',
      createAiAssistant: 'Create your AI assistant',
      provisionPhoneNumber: 'Provision a phone number',
      configureSystemPrompt: 'Configure the system prompt',
      createAssistant: 'Create Assistant',
      creatingAssistant: 'Setting up your AI assistant...',
      creatingYourAiAssistant: 'Creating Your AI Assistant',
      assistantDescription: 'We\'re setting up your personalized AI assistant that will handle phone calls.',
      creatingAssistantLoading: 'Creating assistant...',
      phoneNumber: 'Phone Number',
      gettingPhoneNumber: 'Getting your phone number...',
      provisionPhoneNumberTitle: 'Provision Phone Number',
      chooseAreaCode: 'Choose your preferred area code for the phone number.',
      areaCode: 'Area Code',
      tryingAreaCode: 'Trying area code {areaCode}...',
      systemPrompt: 'System Prompt',
      configuringAssistant: 'Configuring your assistant...',
      systemPromptReady: 'System Prompt Ready',
      promptConfigured: 'Your assistant has been configured with a professional medical clinic prompt.',
      defaultConfiguration: 'Default Configuration:',
      professionalMedicalAssistant: 'Professional medical assistant',
      handlesGeneralInquiries: 'Handles general inquiries',
      directsEmergenciesTo911: 'Directs emergencies to 911',
      refersMedicalQuestions: 'Refers medical questions to providers',
      customizeLater: 'You can customize this later in the System Prompt section.',
      complete: 'Complete!',
      assistantReady: 'Your assistant is ready to go!',
      setupComplete: 'Setup Complete!',
      assistantReadyDescription: 'Your AI assistant is now ready to handle phone calls for your clinic.',
      whatsNext: 'What\'s Next:',
      monitorCallLogs: 'Monitor call logs in the dashboard',
      customizeSystemPrompt: 'Customize the system prompt as needed',
      uploadKnowledgeFiles: 'Upload knowledge base files',
      assistantReadyToTakeCalls: 'Your assistant is ready to take calls!',
      stepOf: 'Step {current} of {total}',
      pleaseWait: 'Please wait...',
      completeSetup: 'Complete Setup',
      letsSetupAssistant: 'Let\'s set up your AI phone assistant',
      failedToCreateAssistant: 'Failed to create assistant',
      failedToProvisionPhone: 'Failed to provision phone number',
      phoneProvisioned: 'Phone Number Provisioned',
      phoneProvisionedMessage: 'Phone number successfully provisioned!\n\nNote: Your requested area code ({requestedAreaCode}) was not available, so we provisioned a number with area code {successfulAreaCode} instead.',
      attemptedAreaCodes: 'Attempted area codes: {areaCodes}'
    },

    // Modal
    modal: {
      confirm: 'Confirm',
      cancel: 'Cancel',
      ok: 'OK',
      delete: 'Delete',
      reset: 'Reset',
      update: 'Update',
      save: 'Save',
      close: 'Close'
    },

    // Status
    status: {
      completed: 'Completed',
      busy: 'Busy',
      noAnswer: 'No Answer',
      failed: 'Failed',
      active: 'Active',
      inactive: 'Inactive',
      enabled: 'Enabled',
      disabled: 'Disabled',
      available: 'Available',
      unavailable: 'Unavailable'
    },

    // Error Messages
    errors: {
      fileTooLarge: 'File too large. Maximum size is 10MB.',
      internalServerError: 'Internal server error',
      anErrorOccurred: 'An error occurred',
      networkError: 'Network error',
      unauthorized: 'Unauthorized',
      forbidden: 'Forbidden',
      notFound: 'Not found',
      validationError: 'Validation error',
      serverError: 'Server error'
    }
  },

  es: {
    // Common
    common: {
      loading: 'Cargando...',
      error: 'Error',
      success: 'Éxito',
      confirm: 'Confirmar',
      cancel: 'Cancelar',
      delete: 'Eliminar',
      save: 'Guardar',
      update: 'Actualizar',
      create: 'Crear',
      refresh: 'Actualizar',
      logout: 'Cerrar Sesión',
      back: 'Atrás',
      next: 'Siguiente',
      continue: 'Continuar',
      complete: 'Completar',
      ok: 'OK',
      yes: 'Sí',
      no: 'No',
      close: 'Cerrar',
      edit: 'Editar',
      view: 'Ver',
      upload: 'Subir',
      download: 'Descargar',
      search: 'Buscar',
      filter: 'Filtrar',
      sort: 'Ordenar',
      reset: 'Restablecer',
      submit: 'Enviar',
      login: 'Iniciar Sesión',
      register: 'Registrar',
      signIn: 'Iniciar Sesión',
      signUp: 'Registrarse',
      email: 'Correo Electrónico',
      password: 'Contraseña',
      name: 'Nombre',
      phone: 'Teléfono',
      address: 'Dirección',
      date: 'Fecha',
      time: 'Hora',
      status: 'Estado',
      duration: 'Duración',
      from: 'De',
      to: 'Para',
      description: 'Descripción',
      message: 'Mensaje',
      title: 'Título',
      content: 'Contenido',
      file: 'Archivo',
      files: 'Archivos',
      size: 'Tamaño',
      type: 'Tipo',
      created: 'Creado',
      updated: 'Actualizado',
      uploaded: 'Subido',
      deleted: 'Eliminado',
      active: 'Activo',
      inactive: 'Inactivo',
      enabled: 'Habilitado',
      disabled: 'Deshabilitado',
      available: 'Disponible',
      unavailable: 'No Disponible',
      required: 'Requerido',
      optional: 'Opcional',
      all: 'Todos',
      none: 'Ninguno',
      select: 'Seleccionar',
      choose: 'Elegir',
      browse: 'Explorar',
      clear: 'Limpiar',
      apply: 'Aplicar',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      warning: 'Advertencia',
      info: 'Información',
      notice: 'Aviso',
      alert: 'Alerta',
      notification: 'Notificación',
      help: 'Ayuda',
      support: 'Soporte',
      contact: 'Contacto',
      about: 'Acerca de',
      settings: 'Configuración',
      preferences: 'Preferencias',
      profile: 'Perfil',
      account: 'Cuenta',
      dashboard: 'Panel de Control',
      home: 'Inicio',
      menu: 'Menú',
      navigation: 'Navegación',
      language: 'Idioma',
      english: 'Inglés',
      spanish: 'Español',
      selectLanguage: 'Seleccionar Idioma',
      changeLanguage: 'Cambiar Idioma'
    },

    // Navigation
    nav: {
      clinicAssistant: 'Asistente de Clínica TechMaven',
      adminPanel: 'Panel de Administración',
      clinicName: 'Nombre de la Clínica'
    },

    // Login/Register
    auth: {
      signInToClinic: 'Iniciar sesión en su clínica',
      registerClinic: 'Registrar su clínica',
      adminPanel: 'Panel de Administración',
      signInToAdmin: 'Iniciar sesión para administrar clínicas y configuraciones del sistema',
      adminEmail: 'Correo del Administrador',
      clinicName: 'Nombre de la Clínica',
      needToRegister: '¿Necesita registrarse?',
      alreadyHaveAccount: '¿Ya tiene una cuenta?',
      adminAccessRequired: 'Se requiere acceso de administrador',
      loginFailed: 'Error al iniciar sesión',
      signingIn: 'Iniciando sesión...',
      signInToAdminPanel: 'Iniciar sesión en el Panel de Administración',
      invalidCredentials: 'Credenciales inválidas',
      emailPasswordRequired: 'Se requieren correo electrónico y contraseña',
      emailPasswordClinicRequired: 'Se requieren correo electrónico, contraseña y nombre de la clínica',
      passwordMinLength: 'La contraseña debe tener al menos 8 caracteres',
      clinicExists: 'Ya existe una clínica con este correo electrónico',
      clinicRegistered: 'Clínica registrada exitosamente',
      loginSuccessful: 'Inicio de sesión exitoso',
      internalServerError: 'Error interno del servidor'
    },

    // Dashboard
    dashboard: {
      callLogs: 'Registro de Llamadas',
      recentCalls: 'Llamadas telefónicas recientes y conversaciones',
      refreshing: 'Actualizando...',
      recentCallsTitle: 'Llamadas Recientes',
      autoRefresh: 'Se actualiza automáticamente cada 30 segundos',
      loadingCalls: 'Cargando llamadas...',
      noCallsYet: 'Aún no hay llamadas',
      callsWillAppear: 'Las llamadas aparecerán aquí una vez que su asistente comience a recibirlas',
      systemStatus: 'Estado del Sistema',
      assistant: 'Asistente',
      phone: 'Teléfono',
      prompt: 'Prompt',
      setup: 'Configuración',
      complete: 'Completo',
      quickActions: 'Acciones Rápidas',
      createAssistant: 'Crear Asistente',
      setupAiToHandleCalls: 'Configurar IA para manejar llamadas',
      systemPrompt: 'Prompt del Sistema',
      configured: 'Configurado',
      notConfigured: 'No configurado',
      knowledgeBase: 'Base de Conocimiento',
      uploadFiles: 'Subir archivos',
      tools: 'Herramientas',
      manageTools: 'Administrar herramientas',
      phoneNumber: 'Número de Teléfono',
      notProvisioned: 'No aprovisionado',
      areaCode: 'Código de área',
      new: 'Nuevo',
      deleting: 'Eliminando...',
      getNumber: 'Obtener Número',
      getting: 'Obteniendo...',
      get: 'Obtener',
      systemInfo: 'Información del Sistema',
      model: 'Modelo',
      voice: 'Voz',
      clinic: 'Clínica',
      promptPreview: 'Prompt',
      tryingAreaCode: 'Probando código de área {areaCode}...',
      phoneProvisioned: 'Número de Teléfono Aprovisionado',
      phoneProvisionedMessage: '¡Número de teléfono aprovisionado exitosamente!\n\nNota: Su código de área solicitado ({requestedAreaCode}) no estaba disponible, por lo que aprovisionamos un número con código de área {successfulAreaCode} en su lugar.',
      confirmDelete: 'Confirmar Eliminación',
      confirmDeleteMessage: '¿Está seguro de que desea eliminar este número de teléfono? Esta acción no se puede deshacer.',
      phoneDeleted: 'Número de teléfono eliminado exitosamente. Ahora puede aprovisionar un nuevo número.',
      failedToCreateAssistant: 'Error al crear asistente',
      failedToProvisionPhone: 'Error al aprovisionar número de teléfono',
      failedToDeletePhone: 'Error al eliminar número de teléfono',
      vapiAuthFailed: 'Error de autenticación de la API VAPI'
    },

    // System Prompt
    systemPrompt: {
      configureSystemPrompt: 'Configurar Prompt del Sistema',
      backToDashboard: '← Volver al Panel de Control',
      loadingCurrentPrompt: 'Cargando prompt actual...',
      systemPromptLabel: 'Prompt del Sistema',
      enterSystemPrompt: 'Ingrese el prompt del sistema de su asistente aquí...',
      characters: 'Caracteres',
      saving: 'Guardando...',
      savePrompt: 'Guardar Prompt',
      systemPromptUpdated: '¡Prompt del sistema actualizado exitosamente!',
      failedToUpdatePrompt: 'Error al actualizar prompt del sistema'
    },

    // Knowledge Base
    knowledge: {
      uploadKnowledgeBaseFiles: 'Subir Archivos de Base de Conocimiento',
      backToDashboard: '← Volver al Panel de Control',
      selectFile: 'Seleccionar Archivo (solo PDF o TXT, máx. 10MB)',
      selected: 'Seleccionado',
      uploading: 'Subiendo...',
      uploadFile: 'Subir Archivo',
      fileUploaded: '¡Archivo subido exitosamente!',
      failedToUpload: 'Error al subir archivo',
      uploadedFiles: 'Archivos Subidos',
      noFilesUploaded: 'Aún no se han subido archivos',
      uploaded: 'Subido',
      confirmDeleteFile: '¿Está seguro de que desea eliminar este archivo?',
      fileDeleted: 'Archivo eliminado exitosamente',
      failedToDeleteFile: 'Error al eliminar archivo',
      noAssistantFound: 'No se encontró asistente. Por favor, cree un asistente primero.',
      onlyPdfTxtAllowed: 'Solo se permiten archivos PDF y TXT',
      noFileUploaded: 'No se subió ningún archivo',
      pleaseSelectFile: 'Por favor seleccione un archivo',
      fileNotFound: 'Archivo no encontrado',
      failedToGetFiles: 'Error al obtener archivos'
    },

    // Tools Management
    tools: {
      manageTools: 'Administrar Herramientas del Asistente',
      backToDashboard: '← Volver al Panel de Control',
      addTool: 'Agregar Herramienta',
      editTool: 'Editar Herramienta',
      toolType: 'Tipo de Herramienta',
      functionTool: 'Herramienta de Función',
      webhookTool: 'Herramienta Webhook',
      functionName: 'Nombre de la Función',
      functionDescription: 'Descripción de la Función',
      parameters: 'Parámetros',
      addParameter: 'Agregar Parámetro',
      enterParameterName: 'Ingrese el nombre del parámetro:',
      required: 'Requerido',
      noParameters: 'No hay parámetros definidos',
      serverUrl: 'URL del Servidor',
      serverSecret: 'Secreto del Servidor',
      loadingTools: 'Cargando herramientas...',
      noTools: 'No hay herramientas configuradas',
      noToolsDescription: 'Agregue herramientas para extender las capacidades de su asistente',
      unnamedTool: 'Herramienta Sin Nombre',
      noDescription: 'No hay descripción proporcionada',
      confirmDelete: 'Confirmar Eliminación',
      confirmDeleteMessage: '¿Está seguro de que desea eliminar esta herramienta? Esta acción no se puede deshacer.',
      toolAdded: '¡Herramienta agregada exitosamente!',
      toolUpdated: '¡Herramienta actualizada exitosamente!',
      toolDeleted: '¡Herramienta eliminada exitosamente!',
      failedToSaveTool: 'Error al guardar herramienta',
      failedToDeleteTool: 'Error al eliminar herramienta',
      failedToFetchTools: 'Error al obtener herramientas',
      toolTemplates: 'Plantillas de Herramientas',
      webhookTemplate: 'Herramienta Webhook',
      webhookDescription: 'Conectar a endpoint webhook externo',
      addEmailTool: 'Agregar Herramienta de Notificación por Email',
      emailToolAdded: '¡Herramienta de notificación por email agregada exitosamente!',
      failedToAddEmailTool: 'Error al agregar herramienta de notificación por email'
    },

    // Admin Dashboard
    admin: {
      loadingAdminPanel: 'Cargando panel de administración...',
      clinics: 'Clínicas',
      systemPrompt: 'Prompt del Sistema',
      allClinics: 'Todas las Clínicas',
      manageClinics: 'Administrar todas las clínicas registradas y sus datos',
      hasAssistant: '✅ Asistente',
      noAssistant: '❌ Sin Asistente',
      hasPhone: '✅ Teléfono',
      noPhone: '❌ Sin Teléfono',
      deleteClinic: 'Eliminar Clínica',
      deleteClinicMessage: '¿Está seguro de que desea eliminar "{clinicName}"? Esto eliminará permanentemente todos los datos asociados incluyendo asistentes, números de teléfono y registros de llamadas.',
      clinicDeleted: 'Clínica eliminada exitosamente',
      failedToDeleteClinic: 'Error al eliminar clínica',
      defaultSystemPrompt: 'Prompt del Sistema por Defecto',
      defaultPromptDescription: 'Este prompt se utilizará para todos los nuevos asistentes creados por las clínicas.',
      systemPromptLabel: 'Prompt del Sistema',
      enterDefaultPrompt: 'Ingrese el prompt del sistema por defecto...',
      resetToOriginal: 'Restablecer al Original',
      updating: 'Actualizando...',
      updatePrompt: 'Actualizar Prompt',
      defaultPromptUpdated: 'Prompt del sistema por defecto actualizado exitosamente',
      failedToUpdatePrompt: 'Error al actualizar prompt del sistema',
      resetSystemPrompt: 'Restablecer Prompt del Sistema',
      resetPromptMessage: '¿Está seguro de que desea restablecer el prompt del sistema al original por defecto?',
      promptReset: 'Prompt del sistema restablecido al original por defecto',
      failedToResetPrompt: 'Error al restablecer prompt del sistema',
      failedToFetchPrompt: 'Error al obtener prompt por defecto'
    },

    // Setup Wizard
    setupWizard: {
      welcome: '¡Bienvenido!',
      welcomeDescription: 'Configuremos su asistente de clínica en solo unos pasos.',
      welcomeToClinic: 'Bienvenido a {clinicName}',
      setupDescription: 'Le ayudaremos a configurar su asistente telefónico de IA en solo 3 pasos simples.',
      whatWellSetup: 'Lo que configuraremos:',
      createAiAssistant: 'Crear su asistente de IA',
      provisionPhoneNumber: 'Aprovisionar un número de teléfono',
      configureSystemPrompt: 'Configurar el prompt del sistema',
      createAssistant: 'Crear Asistente',
      creatingAssistant: 'Configurando su asistente de IA...',
      creatingYourAiAssistant: 'Creando Su Asistente de IA',
      assistantDescription: 'Estamos configurando su asistente de IA personalizado que manejará las llamadas telefónicas.',
      creatingAssistantLoading: 'Creando asistente...',
      phoneNumber: 'Número de Teléfono',
      gettingPhoneNumber: 'Obteniendo su número de teléfono...',
      provisionPhoneNumberTitle: 'Aprovisionar Número de Teléfono',
      chooseAreaCode: 'Elija su código de área preferido para el número de teléfono.',
      areaCode: 'Código de Área',
      tryingAreaCode: 'Probando código de área {areaCode}...',
      systemPrompt: 'Prompt del Sistema',
      configuringAssistant: 'Configurando su asistente...',
      systemPromptReady: 'Prompt del Sistema Listo',
      promptConfigured: 'Su asistente ha sido configurado con un prompt profesional de clínica médica.',
      defaultConfiguration: 'Configuración por Defecto:',
      professionalMedicalAssistant: 'Asistente médico profesional',
      handlesGeneralInquiries: 'Maneja consultas generales',
      directsEmergenciesTo911: 'Dirige emergencias al 911',
      refersMedicalQuestions: 'Refiere preguntas médicas a proveedores',
      customizeLater: 'Puede personalizar esto más tarde en la sección de Prompt del Sistema.',
      complete: '¡Completo!',
      assistantReady: '¡Su asistente está listo!',
      setupComplete: '¡Configuración Completa!',
      assistantReadyDescription: 'Su asistente de IA ahora está listo para manejar llamadas telefónicas para su clínica.',
      whatsNext: 'Qué Sigue:',
      monitorCallLogs: 'Monitorear registros de llamadas en el panel de control',
      customizeSystemPrompt: 'Personalizar el prompt del sistema según sea necesario',
      uploadKnowledgeFiles: 'Subir archivos de base de conocimiento',
      assistantReadyToTakeCalls: '¡Su asistente está listo para recibir llamadas!',
      stepOf: 'Paso {current} de {total}',
      pleaseWait: 'Por favor espere...',
      completeSetup: 'Completar Configuración',
      letsSetupAssistant: 'Configuremos su asistente telefónico de IA',
      failedToCreateAssistant: 'Error al crear asistente',
      failedToProvisionPhone: 'Error al aprovisionar número de teléfono',
      phoneProvisioned: 'Número de Teléfono Aprovisionado',
      phoneProvisionedMessage: '¡Número de teléfono aprovisionado exitosamente!\n\nNota: Su código de área solicitado ({requestedAreaCode}) no estaba disponible, por lo que aprovisionamos un número con código de área {successfulAreaCode} en su lugar.',
      attemptedAreaCodes: 'Códigos de área intentados: {areaCodes}'
    },

    // Modal
    modal: {
      confirm: 'Confirmar',
      cancel: 'Cancelar',
      ok: 'OK',
      delete: 'Eliminar',
      reset: 'Restablecer',
      update: 'Actualizar',
      save: 'Guardar',
      close: 'Cerrar'
    },

    // Status
    status: {
      completed: 'Completado',
      busy: 'Ocupado',
      noAnswer: 'Sin Respuesta',
      failed: 'Fallido',
      active: 'Activo',
      inactive: 'Inactivo',
      enabled: 'Habilitado',
      disabled: 'Deshabilitado',
      available: 'Disponible',
      unavailable: 'No Disponible'
    },

    // Error Messages
    errors: {
      fileTooLarge: 'Archivo demasiado grande. El tamaño máximo es 10MB.',
      internalServerError: 'Error interno del servidor',
      anErrorOccurred: 'Ocurrió un error',
      networkError: 'Error de red',
      unauthorized: 'No autorizado',
      forbidden: 'Prohibido',
      notFound: 'No encontrado',
      validationError: 'Error de validación',
      serverError: 'Error del servidor'
    }
  }
};

// Helper function to get nested translation
export const getTranslation = (translations, key, params = {}) => {
  const keys = key.split('.');
  let value = translations;
  
  for (const k of keys) {
    if (value && typeof value === 'object' && k in value) {
      value = value[k];
    } else {
      return key; // Return key if translation not found
    }
  }
  
  if (typeof value === 'string') {
    // Replace parameters in the string
    return value.replace(/\{(\w+)\}/g, (match, paramKey) => {
      return params[paramKey] || match;
    });
  }
  
  return key;
};
